import { useCallback, useSyncExternalStore } from 'react'

import type { FeatureFlagKey } from '@/lib/feature-flags'
import { FEATURE_FLAGS, resolveFlag } from '@/lib/feature-flags'
import { posthog } from '@/lib/posthog'

/**
 * Explicit developer override via localStorage `ff:<key>` ('1'/'true' → ON,
 * '0'/'false' → OFF). Lets a dev force a flag locally regardless of PostHog or
 * the registry default; absent key → undefined (fall through to the resolver).
 */
function readLocalOverride(key: string): boolean | undefined {
    let raw: string | null = null
    try {
        raw = globalThis.localStorage?.getItem(`ff:${key}`) ?? null
    } catch {
        raw = null
    }
    if (raw === '1' || raw === 'true') return true
    if (raw === '0' || raw === 'false') return false
    return undefined
}

function snapshot(key: FeatureFlagKey): boolean | string {
    const loaded = posthog.__loaded
    return resolveFlag({
        posthogLoaded: loaded,
        posthogValue: loaded ? posthog.getFeatureFlag(key) : undefined,
        localOverride: readLocalOverride(key),
        devDefault: FEATURE_FLAGS[key].devDefault,
        isDev: import.meta.env.DEV,
    })
}

// PostHog is an external mutable store; useSyncExternalStore is the correct
// primitive (no useEffect). onFeatureFlags returns its own unsubscribe.
//
// Fail-safe: posthog may not be initialized yet (no key on prod, headless
// tests) — onFeatureFlags is then absent. Skip the subscription instead of
// crashing every component that reads a flag; the snapshot already resolves to
// a safe OFF/dev-default, and there are simply no live flag updates to observe.
function subscribe(onChange: () => void): () => void {
    if (typeof posthog.onFeatureFlags !== 'function') {
        return () => {}
    }
    return posthog.onFeatureFlags(() => onChange())
}

export function useFeatureFlag(key: FeatureFlagKey): boolean | string {
    const getSnapshot = useCallback(() => snapshot(key), [key])
    // Server snapshot hides the feature (safe default) to avoid hydration mismatch.
    return useSyncExternalStore(subscribe, getSnapshot, () => false)
}
